import { Injectable } from '@angular/core';
import Swal, { SweetAlertResult, SweetAlertOptions } from 'sweetalert2';
import { AuthService } from '../auth/auth.service';

export type ResultadoTipo = 'success' | 'error' | 'warning' | 'info' | 'question';

@Injectable({ providedIn: 'root' })
export class SweetAlertResultService {

  constructor(private authService: AuthService) { }

  /**
   * Muestra un SweetAlert con estilo consistente tipo "resultado".
   * @param tipo     'success' | 'error' | 'warning' | 'info' | 'question'
   * @param titulo   Título principal
   * @param mensaje  Texto secundario descriptivo
   * @param detalle  Texto opcional del badge inferior
   * @param opciones Opciones adicionales para sobrescribir
   */
  mostrar(
    tipo: ResultadoTipo,
    titulo: string,
    mensaje: string,
    detalle?: string,
    opciones: SweetAlertOptions = {}
  ): Promise<SweetAlertResult> {

    const esDark = this.authService?.usuarioConectado?.TemplateConf?.layout?.variant === 'Dark';
    const theme = this.getTheme(esDark);
    const cfg = this.getConfig(tipo);

    const html = `
      <style>${this.getCss(theme)}</style>
      <div class="swal-result sr-${tipo}">
        <div class="sr-icon"><i class="fas ${cfg.icono}"></i></div>
        <div class="sr-title">${titulo}</div>
        <div class="sr-text">${mensaje}</div>
        ${detalle ? `
          <div class="sr-badge">
            <i class="fas ${cfg.iconoBadge}"></i>
            ${detalle}
          </div>` : ''}
      </div>
    `;

    return Swal.fire({
      html,
      width: '480px',
      showConfirmButton: true,
      confirmButtonText: cfg.textoBoton,
      confirmButtonColor: cfg.colorBoton,
      allowOutsideClick: false,
      allowEscapeKey: false,
      background: esDark ? '#1e1e2d' : '#ffffff',
      customClass: { popup: 'swal-result-popup' },
      ...opciones
    });
  }

  /**
   * Muestra un SweetAlert de confirmación (sí/no) con estilo consistente.
   * @returns Promise<boolean> — true si el usuario confirmó, false si canceló.
   */
  async confirmar(
    titulo: string,
    mensaje: string,
    detalle?: string,
    opciones: SweetAlertOptions = {}
  ): Promise<boolean> {

    const esDark = this.authService?.usuarioConectado?.TemplateConf?.layout?.variant === 'Dark';
    const theme = this.getTheme(esDark);
    const cfg = this.getConfig('question');

    const html = `
      <style>${this.getCss(theme)}</style>
      <div class="swal-result sr-question">
        <div class="sr-icon"><i class="fas ${cfg.icono}"></i></div>
        <div class="sr-title">${titulo}</div>
        <div class="sr-text">${mensaje}</div>
        ${detalle ? `
          <div class="sr-badge">
            <i class="fas ${cfg.iconoBadge}"></i>
            ${detalle}
          </div>` : ''}
      </div>
    `;

    const result = await Swal.fire({
      html,
      width: '480px',
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonText: cfg.textoBoton,
      confirmButtonColor: cfg.colorBoton,
      cancelButtonText: 'Cancelar',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      focusConfirm: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      background: esDark ? '#1e1e2d' : '#ffffff',
      customClass: { popup: 'swal-result-popup' },
      ...opciones
    });

    return result.isConfirmed;
  }

  async confirmarConTabla(
    titulo: string,
    mensaje: string,
    encabezados: string[],
    filas: string[][],   // array de filas, cada fila es un array de celdas
    opciones: SweetAlertOptions = {}
  ): Promise<boolean> {

    const esDark = this.authService?.usuarioConectado?.TemplateConf?.layout?.variant === 'Dark';
    const theme = this.getTheme(esDark);
    const cfg = this.getConfig('question');

    const thead = encabezados.map(h => `<th>${h}</th>`).join('');
    const tbody = filas
      .map(fila => `<tr>${fila.map(c => `<td>${c}</td>`).join('')}</tr>`)
      .join('');

    const html = `
    <style>${this.getCss(theme)}${this.getCssTabla(theme)}</style>
    <div class="swal-result sr-question">
      <div class="sr-icon"><i class="fas ${cfg.icono}"></i></div>
      <div class="sr-title">${titulo}</div>
      <div class="sr-text">${mensaje}</div>
    </div>
    <div class="swal-lista">
      <table class="st-tabla">
        <thead><tr>${thead}</tr></thead>
        <tbody>${tbody}</tbody>
      </table>
    </div>
  `;

    const result = await Swal.fire({
      html,
      width: '640px',
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonText: cfg.textoBoton,
      confirmButtonColor: cfg.colorBoton,
      cancelButtonText: 'Cancelar',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      focusConfirm: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      background: esDark ? '#1e1e2d' : '#ffffff',
      customClass: { popup: 'swal-result-popup' },
      ...opciones
    });

    return result.isConfirmed;
  }

  /**
 * Igual que confirmar(), pero el badge aparece ARRIBA del mensaje.
 */
  async confirmarConDetalle(
    titulo: string,
    descripcion: string,
    detalle?: string,
    pregunta?: string,
    opciones: SweetAlertOptions = {}
  ): Promise<boolean> {

    const esDark = this.authService?.usuarioConectado?.TemplateConf?.layout?.variant === 'Dark';
    const theme = this.getTheme(esDark);
    const cfg = this.getConfig('question');

    const html = `
    <style>${this.getCss(theme)}</style>
    <div class="swal-result sr-question">
      <div class="sr-icon"><i class="fas ${cfg.icono}"></i></div>
      <div class="sr-title">${titulo}</div>

      ${descripcion ? `<div class="sr-text">${descripcion}</div>` : ''}

      ${detalle ? `
        <div class="sr-badge" style="margin: 10px 0 14px;">
          <i class="fas ${cfg.iconoBadge}"></i>
          ${detalle}
        </div>` : ''}

      ${pregunta ? `<div class="sr-pregunta">${pregunta}</div>` : ''}
    </div>
  `;

    const result = await Swal.fire({
      html,
      width: '520px',
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonText: cfg.textoBoton,
      confirmButtonColor: cfg.colorBoton,
      cancelButtonText: 'Cancelar',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      focusConfirm: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      background: esDark ? '#1e1e2d' : '#ffffff',
      customClass: { popup: 'swal-result-popup' },
      ...opciones
    });

    return result.isConfirmed;
  }

  // ============================================================
  // Helpers privados
  // ============================================================

  private getTheme(esDark: boolean) {
    return {
      textColor: esDark ? '#eaeaea' : '#2f3349',
      textoSuave: esDark ? 'rgba(234,234,234,0.75)' : 'rgba(47,51,73,0.7)',
      cardBg: esDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
      borderCol: esDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
      hoverRow: esDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'
    };
  }

  private getConfig(tipo: ResultadoTipo) {
    switch (tipo) {
      case 'success':
        return {
          icono: 'fa-check',
          iconoBadge: 'fa-file-invoice',
          textoBoton: 'Aceptar',
          colorBoton: '#28c76f'
        };
      case 'error':
        return {
          icono: 'fa-times',
          iconoBadge: 'fa-exclamation-triangle',
          textoBoton: 'Cerrar',
          colorBoton: '#ea5455'
        };
      case 'warning':
        return {
          icono: 'fa-exclamation',
          iconoBadge: 'fa-exclamation-circle',
          textoBoton: 'Aceptar',
          colorBoton: '#ff9f43'
        };
      case 'question':
        return {
          icono: 'fa-question',
          iconoBadge: 'fa-question-circle',
          textoBoton: 'Sí, continuar',
          colorBoton: '#7367f0'
        };
      case 'info':
      default:
        return {
          icono: 'fa-info',
          iconoBadge: 'fa-info-circle',
          textoBoton: 'Aceptar',
          colorBoton: '#00cfe8'
        };
    }
  }

  private getCss(t: ReturnType<SweetAlertResultService['getTheme']>): string {
    return `
      .swal-result {
        font-family: 'Segoe UI', Roboto, Arial, sans-serif;
        text-align: center;
        color: ${t.textColor};
        padding: 6px 4px;
      }
      .swal-result .sr-icon {
        width: 72px;
        height: 72px;
        margin: 0 auto 14px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 34px;
        animation: sr-pop .35s ease-out;
      }
      .swal-result .sr-pregunta {
        margin-top: 6px;
        padding: 10px 14px;
        background: rgba(115,103,240,.10);
        border-left: 3px solid #7367f0;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 700;
        color: ${t.textColor};
        text-align: left;
      }

      /* ===== Colores por tipo ===== */
      .swal-result.sr-success .sr-icon  { background: rgba(40,199,111,.15); color: #28c76f; box-shadow: 0 0 0 6px rgba(40,199,111,.08); }
      .swal-result.sr-error   .sr-icon  { background: rgba(234,84,85,.15);  color: #ea5455; box-shadow: 0 0 0 6px rgba(234,84,85,.08); }
      .swal-result.sr-warning .sr-icon  { background: rgba(255,159,67,.15); color: #ff9f43; box-shadow: 0 0 0 6px rgba(255,159,67,.08); }
      .swal-result.sr-info    .sr-icon  { background: rgba(0,207,232,.15);  color: #00cfe8; box-shadow: 0 0 0 6px rgba(0,207,232,.08); }
      .swal-result.sr-question .sr-icon { background: rgba(115,103,240,.15); color: #7367f0; box-shadow: 0 0 0 6px rgba(115,103,240,.08); }

      .swal-result .sr-title {
        font-size: 18px;
        font-weight: 700;
        margin-bottom: 6px;
      }

      .swal-result.sr-success  .sr-title { color: #28c76f; }
      .swal-result.sr-error    .sr-title { color: #ea5455; }
      .swal-result.sr-warning  .sr-title { color: #ff9f43; }
      .swal-result.sr-info     .sr-title { color: #00cfe8; }
      .swal-result.sr-question .sr-title { color: #7367f0; }

      .swal-result .sr-text {
        font-size: 13px;
        color: ${t.textoSuave};
        margin-bottom: 12px;
      }

      .swal-result .sr-badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 10px 18px;
        border-radius: 10px;
        font-size: 14px;
        font-weight: 700;
        letter-spacing: .4px;
        margin-top: 4px;
      }

      .swal-result.sr-success  .sr-badge { background: linear-gradient(90deg, rgba(40,199,111,.18), rgba(0,180,216,.18)); border: 1px dashed rgba(40,199,111,.5);  color: #28c76f; }
      .swal-result.sr-error    .sr-badge { background: linear-gradient(90deg, rgba(234,84,85,.18),  rgba(255,108,0,.18)); border: 1px dashed rgba(234,84,85,.5);  color: #ea5455; }
      .swal-result.sr-warning  .sr-badge { background: linear-gradient(90deg, rgba(255,159,67,.18), rgba(255,108,0,.18)); border: 1px dashed rgba(255,159,67,.5); color: #ff9f43; }
      .swal-result.sr-info     .sr-badge { background: linear-gradient(90deg, rgba(0,207,232,.18),  rgba(115,103,240,.18)); border: 1px dashed rgba(0,207,232,.5);  color: #00cfe8; }
      .swal-result.sr-question .sr-badge { background: linear-gradient(90deg, rgba(115,103,240,.18), rgba(0,207,232,.18)); border: 1px dashed rgba(115,103,240,.5); color: #7367f0; }

      @keyframes sr-pop {
        0%   { transform: scale(.5); opacity: 0; }
        70%  { transform: scale(1.1); }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
  }

  /**
 * Muestra una advertencia con una lista/tabla HTML personalizada.
 * Ideal para avisos tipo "catálogos faltantes", "productos pendientes", etc.
 */
  mostrarAdvertenciaLista(
    titulo: string,
    mensaje: string,
    htmlTabla: string,
    encabezado: string = 'Catálogo faltante',
    opciones: SweetAlertOptions = {}
  ): Promise<SweetAlertResult> {

    const esDark = this.authService?.usuarioConectado?.TemplateConf?.layout?.variant === 'Dark';
    const theme = this.getTheme(esDark);
    const cfg = this.getConfig('warning');

    const html = `
    <style>${this.getCss(theme)}${this.getCssTabla(theme)}</style>
    <div class="swal-result sr-warning">
      <div class="sr-icon"><i class="fas ${cfg.icono}"></i></div>
      <div class="sr-title">${titulo}</div>
      <div class="sr-text">${mensaje}</div>
    </div>
    <div class="swal-lista">
      <table class="st-tabla">
        <thead>
          <tr><th>${encabezado}</th></tr>
        </thead>
        <tbody>
          ${htmlTabla}
        </tbody>
      </table>
    </div>
  `;

    return Swal.fire({
      html,
      width: '600px',
      showConfirmButton: true,
      confirmButtonText: cfg.textoBoton,
      confirmButtonColor: cfg.colorBoton,
      allowOutsideClick: false,
      allowEscapeKey: false,
      background: esDark ? '#1e1e2d' : '#ffffff',
      customClass: { popup: 'swal-result-popup' },
      ...opciones
    });
  }

  private getCssTabla(t: ReturnType<SweetAlertResultService['getTheme']>): string {
    return `
    .swal-lista {
      margin: 14px -30px 0 -30px;      /* extiende la tabla hacia los lados */
      text-align: left;
      max-height: 40vh;
      overflow-y: auto;
      padding: 0 4px;                   /* pequeño respiro interno */
    }
    .swal-lista .st-tabla {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
      color: ${t.textColor};
      table-layout: auto;
    }
    .swal-lista .st-tabla thead th {
      text-align: left;
      padding: 8px 10px;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: .5px;
      color: #ff9f43;
      border-bottom: 2px solid rgba(255,159,67,.4);
      white-space: nowrap;
    }
    .swal-lista .st-tabla tbody td {
      padding: 7px 10px;
      border-bottom: 1px solid ${t.borderCol};
      white-space: nowrap;
    }
    .swal-lista .st-tabla tbody tr:hover {
      background: ${t.hoverRow};
    }

    /* ===== Clases utilitarias para celdas ===== */
    .swal-lista .cell-bold    { font-weight: 700; }
    .swal-lista .cell-mono    { font-family: 'Consolas', 'Courier New', monospace; letter-spacing: .3px; }
    .swal-lista .cell-primary { color: #ffa657; font-weight: 700; }   /* naranja */
    .swal-lista .cell-success { color: #28c76f; font-weight: 700; }   /* verde */
    .swal-lista .cell-danger  { color: #ea5455; font-weight: 700; }   /* rojo */
    .swal-lista .cell-muted   { color: ${t.textoSuave}; font-size: 12px; }
    .swal-lista .cell-right   { text-align: right; }
    .swal-lista .cell-center  { text-align: center; }

    /* ===== Alineación automática del último header (Total) ===== */
    .swal-lista .st-tabla thead th:last-child { text-align: right; }
  `;
  }
}